import { createRouter, createWebHistory, type RouteRecordRaw } from 'vue-router'
import { useAuth } from '@/composables/useAuth'
import { useKundenRechte } from '@/composables/useKundenRechte'
import { setBaseTitle } from '@/lib/document-title'

const routes: RouteRecordRaw[] = [
  {
    path: '/',
    redirect: '/projects',
  },
  {
    path: '/login',
    name: 'login',
    component: () => import('@/views/LoginView.vue'),
    meta: { titel: 'login.title', shell: false, public: true },
  },
  {
    // Der Kommentar-Link für Kunden (Welle 4d, Abschnitt D). Die zweite und
    // einzige weitere Seite ohne Anmeldung — sie löst den Token ein und wird
    // damit selbst zur Sitzung. `shell: false`: kein Menü, kein Projektwechsel,
    // nichts von unserem Werkzeug.
    path: '/gast/:token',
    name: 'gast',
    component: () => import('@/views/GastView.vue'),
    meta: { titel: 'ui.ihre_kommentare', shell: false, public: true },
  },
  {
    // Bestätigungslink für die Empfängeradresse eines Kontaktformulars
    // (Ausbau H). Ohne Anmeldung, ohne Menü: Den Link öffnet der Kunde.
    path: '/empfaenger-bestaetigen/:token',
    name: 'empfaenger-bestaetigen',
    component: () => import('@/views/EmpfaengerBestaetigenView.vue'),
    meta: { titel: 'empfaenger.seitentitel', shell: false, public: true },
  },
  {
    path: '/projects',
    name: 'projects',
    component: () => import('@/views/ProjectsView.vue'),
    meta: { titel: 'nav.projects', shell: true },
  },
  {
    // Protokoll: wer hat wann was getan. Nur lesen.
    path: '/audit',
    name: 'audit',
    component: () => import('@/views/AuditView.vue'),
    meta: { titel: 'nav.audit', shell: true },
  },
  {
    path: '/settings',
    name: 'settings',
    component: () => import('@/views/SettingsView.vue'),
    meta: { titel: 'nav.settings', shell: true },
  },
  {
    // Konto-Selbstverwaltung (Passwort, 2FA, Theme) — für alle Rollen,
    // im Kiosk der einzige Weg neben dem Katalog.
    path: '/account',
    name: 'account',
    component: () => import('@/views/AccountView.vue'),
    meta: { titel: 'nav.konto', shell: true },
  },
  {
    // Phase Y.7: Direkter Sprung in die Edit-View (erste Page = index).
    // Page-Wahl danach via Dropdown im Editor selbst.
    path: '/projects/:id',
    redirect: (to) => `/projects/${to.params.id}/edit`,
  },
  {
    // Legacy/Bookmark-Pfad — zeigt die alte Page-Liste falls explizit angefordert.
    path: '/projects/:id/overview',
    name: 'project-detail',
    component: () => import('@/views/ProjectDetailView.vue'),
    props: true,
    meta: { titel: 'nav.projekt', shell: true },
  },
  {
    path: '/projects/:id/edit',
    name: 'project-edit',
    component: () => import('@/views/ProjectEditView.vue'),
    props: true,
    meta: { titel: 'nav.bearbeiten', shell: true, fullscreen: true },
  },
  {
    // Phase C: Produkt-/Katalog-Manager. :collection optional → erste Collection.
    path: '/projects/:id/collections/:collection?',
    name: 'collections',
    component: () => import('@/views/CollectionsView.vue'),
    props: true,
    meta: { titel: 'nav.kataloge', shell: true },
  },
  // Marken-Farben (Admin-only, Server erzwingt). Nur wo der Schalter gesetzt
  // ist — sonst existiert die Route gar nicht, auch nicht per Direktaufruf.
  ...(import.meta.env.VITE_FEATURE_THEME === '1'
    ? [{
        path: '/projects/:id/theme',
        name: 'theme',
        component: () => import('@/views/ThemeView.vue'),
        props: true,
        meta: { titel: 'nav.farben', shell: true },
      }]
    : []),
  {
    path: '/:pathMatch(.*)*',
    name: 'not-found',
    component: () => import('@/views/NotFoundView.vue'),
    meta: { titel: 'nav.nicht_gefunden', shell: true },
  },
]

const router = createRouter({
  history: createWebHistory('/'),
  routes,
})

router.beforeEach(async (to) => {
  const { user, ready, bootstrap } = useAuth()
  if (!ready.value) {
    await bootstrap()
  }
  if (to.meta.public === true) {
    // If already signed-in and trying to reach /login, send to the right home.
    if (to.name === 'login' && user.value) {
      return (await clientHome(user.value, useKundenRechte().rechte.value ?? [])) ?? { name: 'projects' }
    }
    return true
  }
  if (!user.value) {
    return { name: 'login', query: { next: to.fullPath } }
  }

  // Gast-Käfig: Wer über einen Kommentar-Link hereinkam, bleibt auf seiner
  // eigenen Seite. Serverseitig ist das ohnehin dicht (Erlaubnisliste in der
  // AuthMiddleware) — das hier verhindert nur, dass eine falsche Adresse in
  // einer leeren Oberfläche endet statt auf der eigenen Seite.
  if (user.value.role === 'gast') {
    return to.name === 'gast' ? true : false
  }

  // Kunden-Käfig: Rolle `client` darf nur ihren Katalog + die Konto-Seite
  // erreichen. Alles andere (Projektliste, Editor, Settings, fremde Projekte)
  // wird in den eigenen Katalog umgeleitet. Serverseitig ist der Zugriff
  // ohnehin per AccessControl begrenzt — das hier ist die UI-Führung.
  if (user.value.role === 'client') {
    const pid = user.value.projects?.[0]
    /* Einmal fragen, was er darf. Davon haengt ab, wo er landet — und die
       Leiste im Editor holt sich dieselbe Antwort aus dem Zwischenspeicher. */
    const rechte = pid ? await useKundenRechte().laden(pid) : []
    const home = await clientHome(user.value, rechte)
    /* Seit dem 15.08.2026 darf ein Kundenzugang auch an die Texte, wenn das
       Projekt es erlaubt. Der Kaefig laesst die Strecke deshalb durch; OB er
       dort etwas aendern darf, entscheidet der Server (`ModulStore`), und die
       Leiste zeigt ihm nur, was er darf. Ein Kunde ohne das Recht landet in
       einer Ansicht, in der es nichts zu speichern gibt -- das ist die
       Fuehrung, nicht die Grenze. */
    const eigenes = !!pid && to.params.id === pid
    /* Den Katalog gibt es seit dem 02.10.2026 nur mit dem Recht „Katalog
       pflegen". Ohne es gaebe es dort nur Knoepfe, die der Server ablehnt. */
    const allowed =
      to.name === 'account' ||
      (eigenes && to.name === 'project-edit') ||
      (eigenes && to.name === 'collections' && rechte.includes('katalog'))
    if (!allowed) {
      return home ?? { name: 'account' }
    }
  }
  return true
})

/**
 * Wo ein Kundenzugang landet.
 *
 * Wer seine Texte pflegen darf, landet im Editor. Wer nicht, landet im
 * Katalog — aber nur, wenn er ihn pflegen darf (Recht `katalog`, seit dem
 * 02.10.2026) und es dort etwas gibt. Sonst wieder im Editor: Dort
 * sieht er wenigstens seine Seite und kann kommentieren. Baiboon landete am
 * 15.08.2026 in einem Katalog mit „Keine Einträge" und hatte damit eine Seite
 * ohne jeden Sinn vor sich (David: „da ist nichts zum editieren").
 *
 * Ohne zugewiesenes Projekt geht es zum Konto — eine Fehlkonfiguration soll
 * man sehen, nicht umgehen.
 */
async function clientHome(
  user: { role: string; projects: readonly string[] | null },
  rechte: readonly string[] = [],
): Promise<{ name: string; params?: Record<string, string> } | null> {
  if (user.role !== 'client') return null
  const pid = user.projects?.[0]
  if (!pid) return { name: 'account' }
  if (rechte.includes('edit')) return { name: 'project-edit', params: { id: pid } }
  return rechte.includes('katalog') && (await useKundenRechte().hatKataloge(pid))
    ? { name: 'collections', params: { id: pid } }
    : { name: 'project-edit', params: { id: pid } }
}

router.afterEach((to) => {
  // Suffix (Standard-Produkt vs. Kundenname) kommt aus dem Branding,
  // siehe applyBranding() → setBrandName() in composables/useBranding.ts.
  setBaseTitle(to.meta.titel as string | undefined)
})

export default router
